import {Icon} from '@wandb/weave/components/Icon';
import {Radio} from '@wandb/weave/components/Radio';
import React from 'react';

import {Banner} from '../../../Banner/Banner';
import {ClearSettingsRows} from '../components/ClearSettingsRow';
import {ResetSettingButton} from '../components/ResetSettingButton';
import {useClearSettingRows} from '../hooks/useClearSettingRows';
import {TooltipNumberOfRunsOptions} from '../types';
import {isTooltipNumberOfRunsSettingActive} from '../utils';
import {getLinePlotSettingWithDefault} from './linePlotDefaults';
import {useLinePlotSettingsContext} from './LinePlotSettingsContextProvider';

export const LinePlotTooltipNumRuns = () => {
  const {
    clearSetting,
    displayLinePlotSettings,
    linePlotSettings,
    settingLevel,
    settingOverrideCounts,
    trackLinePlotSetting,
    updateLinePlotSettings,
  } = useLinePlotSettingsContext();

  const tooltipNumberOfRuns = getLinePlotSettingWithDefault(
    displayLinePlotSettings?.tooltipNumberOfRuns,
    'tooltipNumberOfRuns'
  );

  const clearSettingRows = useClearSettingRows({
    numOverrides: settingOverrideCounts,
    settingKey: 'tooltipNumberOfRuns',
    settingLevel: 'workspace',
    clearSetting,
    emptySettings: {tooltipNumberOfRuns: undefined},
  });

  return (
    <div className="flex flex-col">
      <fieldset className="text-moon-800">
        <div className="flex items-center justify-between">
          <div className="text-base font-semibold leading-8">
            Number of runs shown in tooltips
          </div>
          <ResetSettingButton
            isEnabled={isTooltipNumberOfRunsSettingActive(linePlotSettings)}
            onButtonClick={() =>
              clearSetting(settingLevel, 'tooltipNumberOfRuns', {
                tooltipNumberOfRuns: undefined,
              })
            }
            settingLevel={settingLevel}
          />
        </div>
        <Radio.Root
          aria-label="Number of runs shown in panels"
          name="tooltip-runs"
          onValueChange={(numRuns: TooltipNumberOfRunsOptions) => {
            updateLinePlotSettings({tooltipNumberOfRuns: numRuns});
            trackLinePlotSetting('tooltipNumberOfRuns', numRuns);
          }}
          value={tooltipNumberOfRuns}>
          <div className="flex items-center justify-start">
            <Radio.Item
              id="tooltip-runs-single-run"
              value={TooltipNumberOfRunsOptions.Single}>
              <Radio.Indicator />
            </Radio.Item>
            <label className="" htmlFor="tooltip-runs-single-run">
              <span className="mx-8">Single run</span>
            </label>
          </div>
          <div className="flex items-center justify-start">
            <Radio.Item
              id="tooltip-runs-default-runs"
              value={TooltipNumberOfRunsOptions.Default}>
              <Radio.Indicator />
            </Radio.Item>
            <label className="" htmlFor="tooltip-runs-default-runs">
              <span className="mx-8">Default</span>
            </label>
          </div>
          <div className="flex items-center justify-start">
            <Radio.Item
              id="tooltip-runs-all-runs"
              value={TooltipNumberOfRunsOptions.All}>
              <Radio.Indicator />
            </Radio.Item>
            <label className="" htmlFor="tooltip-runs-all-runs">
              <span className="mx-8">All runs</span>
            </label>
          </div>
        </Radio.Root>
      </fieldset>
      {tooltipNumberOfRuns === TooltipNumberOfRunsOptions.All && (
        <Banner className="mt-4 justify-start" variant="info">
          <Icon name="info" />
          <span className="ml-8 inline-block">
            Showing all runs may affect performance and usability
          </span>
        </Banner>
      )}
      <ClearSettingsRows rows={clearSettingRows} />
    </div>
  );
};
